import { Breadcrumb, Card } from 'nhsuk-react-components';
import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import SummaryTable from 'components/SummaryTable/Campaign/SummaryTable.tsx';
import IterationSummaryTable from 'components/SummaryTable/Iteration/SummaryTable.tsx';
import useCampaign from 'hooks/useCampaign.ts';
import CampaignPageHeader from 'components/CampaignPageHeader/index.tsx';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import useUserAuthorisation from 'hooks/useUserAuthorisation.ts';
import LockStatus from 'components/LockStatus/index.tsx';
import useCampaignLock from 'hooks/useCampaignLock.ts';
import Confirm from 'components/Confirm/index.tsx';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useAuth from 'hooks/useAuth.ts';
import ProgressIndicator from 'components/ProgressIndicator/index.tsx';
import { ICampaignLevelParams } from 'utils/parameterUtils';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';

function CampaignSummaryPage() {
  const { user } = useAuth();
  const { teamId, campaignId } = useParams() as ICampaignLevelParams;
  const { campaign } = useCampaign();
  const campaignTemplate = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(campaign.CampaignConfig.Template),
  });
  const { canUpdateCampaign } = useUserAuthorisation();
  const { unlockCampaign, error: lockError } = useCampaignLock({ teamId, campaignId });
  const { getCampaignPathFor } = useCampaignNavigate();
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);

  const canUpdate = canUpdateCampaign() && (!campaign.locked_by?.id || user.userId === campaign.locked_by?.id);
  const canUnlock = canUpdateCampaign();
  const lockedBy = campaign.locked_by?.name || 'Unknown user';

  const handleUnlock = () => {
    if (user.userId === campaign.locked_by?.id) {
      unlockCampaign();
    } else {
      setShowUnlockDialog(true);
    }
  };

  const handleUnlockYes = () => {
    unlockCampaign();
    setShowUnlockDialog(false);
  };

  const handleUnlockNo = () => {
    setShowUnlockDialog(false);
  };

  if (!campaign.CampaignConfig.Name) {
    return <ProgressIndicator text="Loading campaign details..." />;
  }

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-6">
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item href="/campaigns">
            Manage campaigns
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span aria-current="page">Campaign summary</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <CampaignPageHeader campaign={campaign} template={campaignTemplate.data} showLink={false} />
      <Card>
        <>
          <div className="nhsuk-grid-row nhsuk-u-margin-bottom-3">
            <div className="nhsuk-grid-column-one-half">
              <Card.Heading>Campaign details</Card.Heading>
            </div>
            <div className="nhsuk-grid-column-one-half nhsuk-u-text-align-right">
              {campaign.locked_by?.id && (
                <div className="nhsuk-body-s">
                  <LockStatus
                    lockedBy={lockedBy}
                    canUnlock={canUnlock}
                    onUnlock={handleUnlock}
                    id={campaign.CampaignConfig.ID}
                  />
                  <ApiErrorDetails apiError={lockError} />
                </div>
              )}
            </div>
          </div>
          <SummaryTable
            campaign={campaign}
            canUpdate={!campaign.locked_by?.id || user.userId === campaign.locked_by?.id}
            template={campaignTemplate.data}
          />
        </>
      </Card>
      <Card>
        <>
          <Card.Heading>Iteration details</Card.Heading>

          {campaign.CampaignConfig.Iterations?.length ? (
            <IterationSummaryTable
              iterations={campaign.CampaignConfig.Iterations}
              userId={user.userId}
            />
          ) : (
            <Card.Description>
              No iterations have been selected for the campaign.
            </Card.Description>
          )}

          {canUpdate && (
            <center className="nhsuk-u-margin-top-5 nhsuk-u-margin-bottom-3">
              <Link
                to={getCampaignPathFor('iteration/new')}
                aria-label="Create a new iteration for the campaign"
              >
                <p className="nhsuk-u-margin-bottom-3">
                  Create a new iteration
                </p>
              </Link>
              <hr className="nhsuk-u-margin-top-2 nhus-u-margin-bottom-5 nhus-u-padding-bottom-5" />
            </center>
          )}
        </>
      </Card>
      {showUnlockDialog && (
        <Confirm
          title="This campaign is locked"
          message={`This campaign is currently locked by ${lockedBy}. If you unlock it and make changes, their work could be overwritten. We recommend checking with them before you continue.
Do you want to unlock the campaign and start editing?`}
          yesText="Yes, unlock and edit"
          noText="No, go back to campaign"
          onYes={handleUnlockYes}
          onNo={handleUnlockNo}
        />
      )}
    </div>
  );
}

export default CampaignSummaryPage;
